import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Avatar, Collapse, Paper, TextField, Button, InputAdornment, Tooltip, Chip, CircularProgress } from '@mui/material';
import { ExpandMore, ChevronRight, Search, UnfoldMore, UnfoldLess, WarningAmber, Groups } from '@mui/icons-material';
import { PageHeader, FilterBar, FilterSpacer, ErrorState, TableEmptyState } from '../../components/common/pageKit';
import hrService, { EmployeeListItem } from '../../services/hrService';

// ---------------------------------------------------------------------------
// Reporting tree built client-side from /hr/employees (manager_id). The
// backend paginates at max 100/page, so every page is fetched and merged
// before the forest is built — the chart needs the whole org, not one page.
// ---------------------------------------------------------------------------

interface OrgNode {
  employee: EmployeeListItem;
  children: OrgNode[];
}

const fullName = (e: { first_name: string; last_name: string | null }): string =>
  [e.first_name, e.last_name].filter(Boolean).join(' ');

const initials = (e: { first_name: string; last_name: string | null }): string =>
  `${e.first_name?.[0] ?? ''}${e.last_name?.[0] ?? ''}`.toUpperCase() || '?';

const avatarPalette = ['#0284c7', '#7c3aed', '#059669', '#d97706', '#db2777', '#0891b2', '#dc2626', '#4f46e5'];
const avatarTint = (id: number): string => avatarPalette[id % avatarPalette.length];

/**
 * Builds a reporting forest from a flat employee list.
 *
 * - Roots: employees with no manager, or whose manager_id points at an
 *   employee not present in the dataset (an "orphan" — e.g. the manager left
 *   or belongs to a filtered-out status).
 * - Cycles: a shared `visited` set means the first traversal to reach a node
 *   claims it; any edge that would revisit an already-claimed node is
 *   dropped rather than followed, so a manager-loop can never recurse
 *   forever and every employee still appears exactly once — as its own root
 *   if the whole loop is otherwise unreachable from a real root.
 */
function buildOrgForest(employees: EmployeeListItem[]): { roots: OrgNode[]; parentOf: Map<number, number | null> } {
  const byId = new Map(employees.map((e) => [e.id, e]));
  const childIdsByManager = new Map<number, number[]>();
  employees.forEach((e) => {
    if (e.manager_id != null && e.manager_id !== e.id && byId.has(e.manager_id)) {
      const arr = childIdsByManager.get(e.manager_id) || [];
      arr.push(e.id);
      childIdsByManager.set(e.manager_id, arr);
    }
  });

  const visited = new Set<number>();
  const parentOf = new Map<number, number | null>();

  const buildNode = (id: number, parentId: number | null): OrgNode => {
    visited.add(id);
    parentOf.set(id, parentId);
    const employee = byId.get(id)!;
    const childIds = childIdsByManager.get(id) || [];
    const children: OrgNode[] = [];
    for (const cid of childIds) {
      if (!visited.has(cid)) children.push(buildNode(cid, id));
    }
    children.sort((a, b) => fullName(a.employee).localeCompare(fullName(b.employee)));
    return { employee, children };
  };

  const roots: OrgNode[] = [];

  employees
    .filter((e) => e.manager_id == null || !byId.has(e.manager_id))
    .sort((a, b) => fullName(a).localeCompare(fullName(b)))
    .forEach((e) => {
      if (!visited.has(e.id)) roots.push(buildNode(e.id, null));
    });

  // Anything still unvisited belongs to a manager-loop with no path from a
  // real root — surface each such loop as its own root (lowest id first) so
  // no employee silently disappears from the chart.
  employees
    .filter((e) => !visited.has(e.id))
    .sort((a, b) => a.id - b.id)
    .forEach((e) => {
      if (!visited.has(e.id)) roots.push(buildNode(e.id, null));
    });

  return { roots, parentOf };
}

interface OrgChartNodeProps {
  node: OrgNode;
  level: number;
  expandedIds: Set<number>;
  onToggle: (id: number) => void;
  onSelect: (id: number) => void;
  matchIds: Set<number>;
  orphan: boolean;
}

const OrgChartNode: React.FC<OrgChartNodeProps> = ({ node, level, expandedIds, onToggle, onSelect, matchIds, orphan }) => {
  const { employee, children } = node;
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(employee.id);
  const isMatch = matchIds.size > 0 && matchIds.has(employee.id);

  return (
    <Box>
      <Paper
        elevation={0}
        onClick={() => onSelect(employee.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onSelect(employee.id);
        }}
        aria-label={`${fullName(employee)}, ${employee.designation_title || 'No designation'}, ${children.length} direct reports`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 1,
          px: 2,
          ml: level * 3,
          mb: 0.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: isMatch ? 'primary.main' : 'divider',
          bgcolor: isMatch ? 'primary.50' : level === 0 ? 'grey.50' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.200', transform: 'translateX(4px)' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
        }}
      >
        <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggle(employee.id);
              }}
              sx={{ p: 0.5 }}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 28 }} />
          )}
        </Box>

        <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: avatarTint(employee.id), fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>
          {initials(employee)}
        </Avatar>

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: level === 0 ? 700 : 600 }}>
            {fullName(employee)}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {employee.designation_title || 'No designation'}
            {employee.dept_name ? ` · ${employee.dept_name}` : ''}
          </Typography>
        </Box>

        {orphan && (
          <Tooltip title="Reports to a manager not found in the employee directory">
            <WarningAmber sx={{ fontSize: 16, color: 'warning.main', mr: 1, flexShrink: 0 }} />
          </Tooltip>
        )}

        <Chip
          size="small"
          variant="outlined"
          label={`${children.length} report${children.length === 1 ? '' : 's'}`}
          sx={{ height: 22, fontSize: '0.6875rem', flexShrink: 0 }}
        />
      </Paper>

      {hasChildren && (
        <Collapse in={isExpanded}>
          <Box>
            {children.map((child) => (
              <OrgChartNode
                key={child.employee.id}
                node={child}
                level={level + 1}
                expandedIds={expandedIds}
                onToggle={onToggle}
                onSelect={onSelect}
                matchIds={matchIds}
                orphan={false}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const OrgChartPage: React.FC = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const fetchAllEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const first = await hrService.getEmployees({ limit: 100, page: 1 });
      let all = [...first.employees];
      const totalPages = first.pagination?.totalPages || 1;
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => hrService.getEmployees({ limit: 100, page: i + 2 }))
        );
        rest.forEach((r) => {
          all = all.concat(r.employees);
        });
      }
      setEmployees(all);
    } catch (err: any) {
      setError(err.message || 'Failed to load the employee directory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllEmployees();
  }, [fetchAllEmployees]);

  const { roots, parentOf } = useMemo(() => buildOrgForest(employees), [employees]);

  const byId = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const orphanIds = useMemo(
    () => new Set(employees.filter((e) => e.manager_id != null && !byId.has(e.manager_id)).map((e) => e.id)),
    [employees, byId]
  );

  // Roots are open by default (mirrors AccountTreeView's level-0-expanded
  // convention); deeper levels start collapsed until the user opens them or
  // a search match pulls their ancestor chain open.
  useEffect(() => {
    setExpandedIds(new Set(roots.map((r) => r.employee.id)));
  }, [roots]);

  const matchIds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return new Set<number>();
    return new Set(
      employees
        .filter(
          (e) =>
            fullName(e).toLowerCase().includes(q) ||
            e.employee_code.toLowerCase().includes(q) ||
            (e.designation_title || '').toLowerCase().includes(q) ||
            (e.dept_name || '').toLowerCase().includes(q)
        )
        .map((e) => e.id)
    );
  }, [employees, search]);

  useEffect(() => {
    if (matchIds.size === 0) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      matchIds.forEach((id) => {
        let cur = parentOf.get(id);
        while (cur != null) {
          next.add(cur);
          cur = parentOf.get(cur) ?? null;
        }
      });
      return next;
    });
  }, [matchIds, parentOf]);

  const expandableIds = useMemo(() => {
    const ids: number[] = [];
    const walk = (nodes: OrgNode[]) => {
      nodes.forEach((n) => {
        if (n.children.length > 0) ids.push(n.employee.id);
        walk(n.children);
      });
    };
    walk(roots);
    return ids;
  }, [roots]);

  const handleSelect = (id: number) => navigate(`/hr/employees/${id}/360`);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Org Chart"
        subtitle={employees.length > 0 ? `${employees.length} employees, ${roots.length} reporting to no one` : 'Reporting structure across the organisation'}
        actions={
          <>
            <Tooltip title="Expand all">
              <IconButton onClick={() => setExpandedIds(new Set(expandableIds))} disabled={loading || expandableIds.length === 0}>
                <UnfoldMore />
              </IconButton>
            </Tooltip>
            <Tooltip title="Collapse all">
              <IconButton onClick={() => setExpandedIds(new Set())} disabled={loading}>
                <UnfoldLess />
              </IconButton>
            </Tooltip>
          </>
        }
      />

      {!loading && !error && employees.length > 0 && (
        <FilterBar>
          <TextField
            size="small"
            placeholder="Search by name, code, designation or department…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          {search && matchIds.size > 0 && (
            <Typography variant="caption" color="text.secondary">
              {matchIds.size} match{matchIds.size === 1 ? '' : 'es'}
            </Typography>
          )}
          {search && matchIds.size === 0 && (
            <Typography variant="caption" color="text.secondary">
              No matches
            </Typography>
          )}
          <FilterSpacer />
          {search && (
            <Button size="small" onClick={() => setSearch('')}>
              Clear
            </Button>
          )}
        </FilterBar>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <ErrorState message={error} onRetry={fetchAllEmployees} />
      ) : employees.length === 0 ? (
        <TableEmptyState title="No employees yet" hint="The org chart will populate once employees are added to the directory." />
      ) : (
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto', py: 1 }}>
          {roots.map((root) => (
            <OrgChartNode
              key={root.employee.id}
              node={root}
              level={0}
              expandedIds={expandedIds}
              onToggle={(id) =>
                setExpandedIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                })
              }
              onSelect={handleSelect}
              matchIds={matchIds}
              orphan={orphanIds.has(root.employee.id)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default OrgChartPage;
